import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Shield, Lock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface ContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  category: string;
  content: string;
  isLoading: boolean;
}

export default function ContentModal({ isOpen, onClose, title, category, content, isLoading }: ContentModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.95 }}
            className="relative w-full max-w-4xl max-h-[90vh] bg-theme-bg border border-gold/30 flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <header className="p-6 md:px-12 md:py-8 border-b border-theme-border flex justify-between items-center bg-black/20">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 border border-gold/30 flex items-center justify-center text-gold">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold">{category} // Secure Briefing</span>
                  <h2 className="text-xl md:text-2xl font-serif italic text-theme-text">{title}</h2>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-gold/10 text-taupe hover:text-gold transition-colors"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </header>

            {/* Scrollable Content */}
            <div className="flex-grow overflow-y-auto p-6 md:p-12 custom-scrollbar">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-24">
                  <div className="w-12 h-12 border-t-2 border-gold rounded-full animate-spin mb-6"></div>
                  <p className="text-[10px] uppercase tracking-[0.5em] text-gold animate-pulse">Decrypting Archive Content...</p>
                </div>
              ) : (
                <div className="markdown-body prose prose-invert max-w-none prose-gold prose-p:text-taupe prose-p:italic prose-h1:text-theme-text prose-h2:text-theme-text prose-h3:text-theme-text prose-strong:text-gold">
                  <ReactMarkdown>{content}</ReactMarkdown>
                </div>
              )}
            </div>

            {/* Footer */}
            <footer className="p-4 bg-black/40 border-t border-theme-border flex justify-between items-center px-12">
               <div className="flex items-center space-x-2 text-[9px] uppercase tracking-widest text-taupe">
                  <Lock className="h-3 w-3" />
                  <span>Confidential Archive // VIP Member Access Only</span>
               </div>
               <button 
                  onClick={onClose}
                  className="text-[9px] uppercase tracking-widest text-gold hover:underline font-bold"
               >
                  Close Briefing
               </button>
            </footer>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
